import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useAngemeldet } from "../../kern/sitzung";
import { useAufgabeLink } from "../../kern/dialog";
import { personNach, type PersonKurz } from "../../kern/personen";
import { anzeigename } from "../../kern/namen";
import { fehlertext } from "../../kern/fehler";
import { AufgabeLink } from "../../kern/ui/AufgabeLink";
import { Kalender, type KalenderSprint } from "../../kern/ui/Kalender";
import { Knopf } from "../../kern/ui/Knopf";
import { Menue, PAPIERKORB, type MenuePunkt } from "../../kern/ui/Menue";
import { Stapel } from "../../kern/ui/Stapel";
import { RESSORT_TOENE, Statuswahl, type StatusOption, type StatusTon } from "../../kern/ui/Statuswahl";
import { Wertwahl, type WertOption } from "../../kern/ui/Wertwahl";
import { useHinweis } from "../../kern/ui/Hinweise";
import { useAufgabenAktionen, useVorhaben } from "./daten";
import { darfBearbeiten, PRIORITAET, statusGesperrt, STATUS_OPTIONEN, type Aufgabe, type Prioritaet, type Status } from "./logik";
import { fristAnzeige, naechsteSortierung, RESSORT_FLAECHEN, sortiereListe, tagVon, titelZeichen, type SortFeld, type Sortierung } from "./listenlogik";
import { ortText, sortierenImSprint } from "./sprintlogik";
import { laufenderSprint, useSprints } from "./sprints";
import { PersonWahl } from "./SprintBoard";
import { useKapsel } from "./vorhaben";
import { startwerte, type ListenVorgabe } from "./vorgaben";
import type { useSchnellaktionen } from "./aktionen";
import type { useLoeschen } from "./loeschen";
import ui from "../../kern/ui/ui.module.css";
import s from "./liste.module.css";

type Schnell = ReturnType<typeof useSchnellaktionen>;
type Loeschen = ReturnType<typeof useLoeschen>;

/** Status in Listen als Zeichen vorne (BAUPLAN-STATUS-IN-LISTEN.md, ADR-023): Optionen wie in der Statuswahl, „Wartet“ im eigenen Ton
 *  (--wartet-ink). Board und Dialog nehmen weiter STATUS_OPTIONEN. */
export const STATUS_ZEILE: readonly StatusOption<Status>[] = STATUS_OPTIONEN.map((o) => (o.wert === "wartet" ? { ...o, ton: "wartet" as const } : o));
/** Priorität in fester Breite. Vorhaben und Ressort füllen ihre Spalte, die mitwächst, bis die Werte passen, und kürzen sonst mit „…“
 *  (BAUPLAN-AUFGABENLISTE-BREITEN.md). */
const PRIO_BREITE = 116;

/** Priorität in der Zeile (Abschnitt 3): ohne Fläche, Fahne Hoch in --danger, Normal dunkel, Niedrig als Umriss */
const PRIO_ZEILE: readonly WertOption<Prioritaet>[] = [
  { wert: "hoch", text: PRIORITAET.hoch, ton: "klar", zeichen: "fahneHoch" },
  { wert: "normal", text: PRIORITAET.normal, ton: "klar", zeichen: "fahne" },
  { wert: "niedrig", text: PRIORITAET.niedrig, ton: "klar", zeichen: "fahneLeer" },
];

/** Zeile „+ Aufgabe hinzufügen“ (Abschnitt 5): Vorgaben wie „Neue Aufgabe“ auf derselben Seite, dazu Person oder Ressort der Gruppe */
export interface NeueZeile {
  vorgabe: ListenVorgabe;
  /** Gruppe nach Status: Die neue Aufgabe bekommt diesen Status (nach dem Anlegen gesetzt, aufgaben_anlegen kennt keinen Status) */
  status?: Status | undefined;
  /** Nach dem Anlegen, zum Beispiel damit Meine Woche die neue Aufgabe in den ersten fünf zeigt */
  onAngelegt?: ((id: string) => void) | undefined;
}

export interface ListeProps {
  liste: readonly Aufgabe[];
  /** Unteraufgaben zum Aufklappen mit dem Pfeil (nur Hauptzeilen) */
  unterVon?: ((id: string) => readonly Aufgabe[]) | undefined;
  /** Name der Tabelle für Screenreader */
  label: string;
  personen: readonly PersonKurz[] | undefined;
  ressorts: readonly { id: string; name: string }[] | undefined;
  farben: ReadonlyMap<string, number> | undefined;
  sa: Schnell;
  loeschen: Loeschen;
  /** Laufender Sprint für den Kalender (getönt, Sprintende in der Schnellwahl) */
  sprint?: KalenderSprint | null | undefined;
  /** Backlog: Spalte „Sprint“ mit „In den Sprint“ vor dem Menü */
  onInSprint?: ((a: Aufgabe) => void) | undefined;
  /** Offen: „Übernehmen“ statt Personenauswahl, solange niemand verantwortlich ist */
  onUebernehmen?: ((a: Aufgabe) => void) | undefined;
  /** Archiv: nur lesen, kein Kalender, kein Löschen, keine Zeile „+ Aufgabe hinzufügen“ */
  nurLesen?: boolean | undefined;
  /** Steht schon in einer Karte: ohne eigenen Rand und Schatten (BAUPLAN-FLAECHEN.md, keine Karte in der Karte) */
  inKarte?: boolean | undefined;
  /** Alle Aufgaben und Offen: wo die Aufgabe liegt */
  zeigeOrt?: boolean | undefined;
  /** Meine Woche: „Unteraufgabe von …“ bei Unteraufgaben in Aufgaben anderer */
  zeigeEltern?: boolean | undefined;
  /** Vorhaben (BAUPLAN-PAKETE.md, Abschnitt 9.3): Spalte „Sprint“ statt Ressort mit „Im Sprint“, sonst „In den Sprint“ (nur mit
   *  sprint.planen, dann ist `onInSprint` gesetzt) oder „Backlog“. */
  sprintSpalte?: { onInSprint?: ((a: Aufgabe) => void) | undefined } | undefined;
  /** Auf der Seite eines Vorhabens: ohne Spalte Vorhaben */
  ohneVorhaben?: boolean | undefined;
  /** Letzte Zeile „+ Aufgabe hinzufügen“, nur wo eine neue Aufgabe mit diesen Vorgaben in derselben Liste erscheint */
  neu?: NeueZeile | undefined;
}

/** Eine Tabelle für alle Listen (AUFGABEN-LISTE.md, Abschnitt 3, BAUPLAN-AUFGABENLISTE.md, BAUPLAN-STATUS-IN-LISTEN.md): Pfeil ·
 *  Statuszeichen · Aufgabe · Priorität · Verantwortlich · Frist · Vorhaben · Ressort · ⋯, im Backlog dazu „Sprint“ vor dem Menü. Bis
 *  899 px (Handy, untere Leiste) schlank: Pfeil, Statuszeichen, Titel, darunter Frist, Köpfe und Vorhaben, der Rest steht im Dialog.
 *  Sortieren nach Frist oder Priorität. */
export function AufgabenListe(props: ListeProps) {
  const { liste, label, onInSprint, sprintSpalte, ohneVorhaben, neu, nurLesen } = props;
  const [sort, setSort] = useState<Sortierung | null>(null);
  const zeilen = sortiereListe(liste, sort);

  function kopf(feld: SortFeld, text: string) {
    const an = sort?.feld === feld;
    return (
      <span role="columnheader" className={[s.kopfZelle, s.kopfSchmal].join(" ")} aria-sort={an ? (sort.richtung === "auf" ? "ascending" : "descending") : "none"}>
        <button type="button" className={s.sortKnopf} onClick={() => setSort(naechsteSortierung(sort, feld))}>
          {text}
          <span aria-hidden="true">{an ? (sort.richtung === "auf" ? "↑" : "↓") : "↕"}</span>
          <span className="nur-leser">, sortieren</span>
        </button>
      </span>
    );
  }

  return (
    <div className={props.inKarte ? s.rahmenInKarte : [ui.karte, s.rahmen].join(" ")}>
      <div role="table" aria-label={label} className={[s.tabelle, ohneVorhaben && s.ohneVorhaben, onInSprint && s.mitSprint].filter(Boolean).join(" ")}>
        <div role="rowgroup">
          <div role="row" className={[s.zeile, s.kopf].join(" ")}>
            <span role="columnheader" className={s.kopfZelle}>
              <span className="nur-leser">Unteraufgaben</span>
            </span>
            <span role="columnheader" className={s.kopfZelle}>
              <span className="nur-leser">Status</span>
            </span>
            <span role="columnheader" className={s.kopfZelle}>
              Aufgabe
            </span>
            {kopf("prioritaet", "Priorität")}
            <span role="columnheader" className={s.kopfZelle}>
              Verantwortlich
            </span>
            {kopf("frist", "Frist")}
            {ohneVorhaben ? null : (
              <span role="columnheader" className={[s.kopfZelle, s.kopfSchmal].join(" ")}>
                Vorhaben
              </span>
            )}
            <span role="columnheader" className={[s.kopfZelle, !sprintSpalte && s.kopfSchmal].filter(Boolean).join(" ")}>
              {sprintSpalte ? "Sprint" : "Ressort"}
            </span>
            {onInSprint ? (
              <span role="columnheader" className={s.kopfZelle}>
                Sprint
              </span>
            ) : null}
            <span role="columnheader" className={s.kopfZelle}>
              <span className="nur-leser">Aktionen</span>
            </span>
          </div>
        </div>
        <div role="rowgroup">
          {zeilen.map((a) => (
            <HauptZeile key={a.id} a={a} sort={sort} {...props} />
          ))}
          {neu && !nurLesen ? <PlusZeile label={label} neu={neu} /> : null}
        </div>
      </div>
    </div>
  );
}

/** Gruppen nach Status (BAUPLAN-STATUS-IN-LISTEN.md, Abschnitt 2, wie ClickUp): „Zu tun“, „In Arbeit“, „Wartet“, „Erledigt“, je mit Kopf
 *  (Pfeil zum Zuklappen, Pille im Ton des Status, Zahl) und eigener Tabelle. „Erledigt“ ist beim Öffnen zu, leere Gruppen fallen weg.
 *  Die Plus-Zeile je Gruppe setzt den Status der Gruppe, in „Erledigt“ gibt es keine. Wechselt eine Aufgabe den Status, wandert sie in
 *  die andere Gruppe, der Hinweis aus statusSetzen sagt wohin. */
export function StatusGruppen({ liste, label, neu, ...rest }: ListeProps) {
  const [auf, setAuf] = useState<Record<Status, boolean>>({ zu_tun: true, in_arbeit: true, wartet: true, erledigt: false });
  const basis = useId();
  return (
    <>
      {STATUS_ZEILE.map((o) => {
        const teil = liste.filter((a) => a.status === o.wert);
        if (teil.length === 0) return null;
        const offen = auf[o.wert];
        const name = `${label}, ${o.text}`;
        const id = `${basis}-${o.wert}`;
        return (
          <section key={o.wert} className={s.gruppe} aria-label={name}>
            <h3 className={s.gruppeKopf}>
              <button type="button" className={s.gruppeKnopf} aria-expanded={offen} aria-controls={id} onClick={() => setAuf({ ...auf, [o.wert]: !offen })}>
                <svg className={s.gruppePfeil} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
                <Statuswahl name="" optionen={STATUS_ZEILE} wert={o.wert} klein />
                <span className={s.gruppeZahl}>
                  {teil.length}
                  <span className="nur-leser"> {teil.length === 1 ? "Aufgabe" : "Aufgaben"}</span>
                </span>
              </button>
            </h3>
            <div id={id} hidden={!offen}>
              {offen ? <AufgabenListe {...rest} liste={teil} label={name} neu={neu && o.wert !== "erledigt" ? { ...neu, status: o.wert } : undefined} /> : null}
            </div>
          </section>
        );
      })}
    </>
  );
}

function HauptZeile({ a, sort, ...p }: { a: Aufgabe; sort: Sortierung | null } & ListeProps) {
  const [auf, setAuf] = useState(false);
  const unter = p.unterVon?.(a.id) ?? [];
  return (
    <>
      <Zeile a={a} unter={unter} auf={auf} onAuf={() => setAuf(!auf)} {...p} />
      {auf ? sortiereListe(sortierenImSprint(unter), sort).map((u) => <Zeile key={u.id} a={u} istUnter {...p} />) : null}
    </>
  );
}

interface ZeileProps extends ListeProps {
  a: Aufgabe;
  istUnter?: boolean;
  unter?: readonly Aufgabe[];
  auf?: boolean;
  onAuf?: () => void;
}

/** Farbe des Punkts vor einem Vorhaben: die Fläche seines Ressorts, wie der Punkt auf der Kachel in Meine Woche */
const punktFarbe = (ressort: string | null | undefined, farben: ReadonlyMap<string, number> | undefined) => {
  const i = ressort ? farben?.get(ressort) : undefined;
  return (i === undefined ? null : RESSORT_FLAECHEN[i]) ?? "var(--dashed)";
};

function Zeile({
  a,
  istUnter,
  unter = [],
  auf,
  onAuf,
  personen,
  ressorts,
  farben,
  sa,
  loeschen,
  sprint,
  onInSprint,
  onUebernehmen,
  nurLesen,
  zeigeOrt,
  zeigeEltern,
  sprintSpalte,
  ohneVorhaben,
}: ZeileProps) {
  const { person: ich, rechte } = useAngemeldet();
  const kapsel = useKapsel();
  const vorhaben = useVorhaben();
  const { zeigen } = useHinweis();
  const navigate = useNavigate();
  const link = useAufgabeLink();
  const geloescht = !!a.archiviert_am;
  const imArchiv = a.sprint_status === "abgeschlossen";
  const alleBearbeiten = rechte.has("aufgaben.alle_bearbeiten");
  const leitet = kapsel.leitet(a.vorhaben_id);
  const darf = darfBearbeiten(a, ich.id, alleBearbeiten, leitet);
  const lesen = !!nurLesen || imArchiv || geloescht;
  const bearbeitbar = darf && !lesen;
  const frist = fristAnzeige(a);
  const fertig = a.status === "erledigt";
  const wieAufgabe = !!istUnter || !!a.eltern_id;
  const gesperrt = statusGesperrt(a, ich.id, alleBearbeiten, leitet);
  const speichern = (aenderung: Record<string, string | null>, danach?: () => void) =>
    sa.akt.bearbeiten.mutate({ id: a.id, aenderung }, { onError: sa.fehler, onSuccess: danach });

  const unterGesamt = istUnter ? 0 : Math.max(a.unteraufgaben_gesamt, unter.length);
  const unterErledigt = istUnter ? 0 : unter.length ? unter.filter((u) => u.status === "erledigt").length : a.unteraufgaben_gesamt - a.unteraufgaben_offen;
  const zeichen = titelZeichen(unterErledigt, unterGesamt, a.kommentare);
  const loescher = geloescht ? personNach(personen, a.geaendert_von) : undefined;
  const meta = [
    zeigeOrt ? ortText(a) : null,
    zeigeEltern && a.eltern_id ? `Unteraufgabe von „${a.eltern_titel ?? "…"}“` : null,
    a.status === "wartet" && a.status_grund ? `Wartet: ${a.status_grund}` : null,
    geloescht && a.archiviert_am ? `gelöscht${loescher ? ` von ${loescher.vorname || anzeigename(loescher)}` : ""}, ${tagVon(a.archiviert_am)}` : null,
  ].filter(Boolean);

  // Ressort als Pille in den Farben der Ressorts, „(inaktiv)“ für ein abgeschaltetes Ressort
  const ressortTon = (id: string): StatusTon => RESSORT_TOENE[farben?.get(id) ?? -1] ?? "neutral";
  const ressortOptionen: WertOption<string>[] = [
    { wert: "", text: "Ohne Ressort", ton: "leise" },
    ...(ressorts ?? []).map((r) => ({ wert: r.id, text: r.name, ton: ressortTon(r.id) })),
    ...(a.ressort_id && !ressorts?.some((r) => r.id === a.ressort_id) ? [{ wert: a.ressort_id, text: "(inaktiv)", ton: ressortTon(a.ressort_id) }] : []),
  ];

  // Vorhaben wie im Dialog (Abschnitt 2): dieselbe Auswahl, ändern nur Vorstand, Leitung des bisherigen oder ohne Vorhaben, kein „Neues …“
  const katalog = vorhaben.data ?? [];
  const vorhabenName = kapsel.name(a.vorhaben_id) ?? katalog.find((v) => v.id === a.vorhaben_id)?.name ?? null;
  const vorhabenRessort = katalog.find((v) => v.id === a.vorhaben_id)?.ressort_id;
  const vorhabenOptionen: WertOption<string>[] = [
    { wert: "", text: "Kein Vorhaben", ton: "leise" },
    ...katalog.map((v) => ({ wert: v.id, text: v.name, punkt: punktFarbe(v.ressort_id, farben) })),
    ...(a.vorhaben_id && !katalog.some((v) => v.id === a.vorhaben_id) ? [{ wert: a.vorhaben_id, text: vorhabenName ?? "…", punkt: punktFarbe(vorhabenRessort, farben) }] : []),
  ];
  const darfVorhaben = bearbeitbar && (alleBearbeiten || !a.vorhaben_id || leitet) && (katalog.length > 0 || !!a.vorhaben_id);

  const fristInhalt = (
    <span className={[s.fristText, frist.leer && s.fristLeer, frist.ueberfaellig && s.fristUeber, frist.fertig && s.fristFertig].filter(Boolean).join(" ")}>
      {frist.text}
      {frist.ueberfaellig ? <small>überfällig</small> : null}
    </span>
  );

  const punkte: MenuePunkt[] = [
    {
      label: "Öffnen",
      ausfuehren: () => {
        const z = link(a.id);
        void navigate(z.to, { state: z.state });
      },
    },
    { label: "Link kopieren", ausfuehren: () => void linkKopieren(a.id, zeigen) },
  ];
  if (darf && geloescht) punkte.push({ label: "Wiederherstellen", ausfuehren: () => loeschen.wiederherstellen(a) });
  else if (darf && !lesen) punkte.push({ label: a.eltern_id ? "Unteraufgabe löschen" : "Aufgabe löschen", gefahr: true, symbol: PAPIERKORB, ausfuehren: () => loeschen.loeschen(a) });

  return (
    <div role="row" className={[s.zeile, istUnter && s.unter, fertig && s.fertig, geloescht && s.geloescht].filter(Boolean).join(" ")}>
      <div role="cell" className={s.cPfeil}>
        {!istUnter && unter.length > 0 ? (
          <button type="button" className={s.aufKnopf} aria-expanded={!!auf} aria-label={`Unteraufgaben von „${a.titel}“ ${auf ? "ausblenden" : "zeigen"}`} onClick={onAuf}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        ) : null}
      </div>
      <div role="cell" className={s.cZeichen}>
        {/* Status vorne als Zeichen, ein Klick öffnet das Statusmenü (wie ClickUp). Ohne Recht mit Grund, im Archiv nur Anzeige. */}
        <Statuswahl
          alsZeichen
          name={`„${a.titel}“`}
          optionen={STATUS_ZEILE}
          wert={a.status}
          darf={!nurLesen && !gesperrt}
          gesperrt={nurLesen ? null : gesperrt}
          onWechsel={(neu) => sa.statusSetzen(a, neu)}
        />
      </div>
      <div role="cell" className={s.cTitel}>
        <div className={s.titelZeile}>
          <AufgabeLink id={a.id} className={s.titel}>
            {a.titel}
          </AufgabeLink>
          {zeichen.unter ? (
            <span className={s.zeichen}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 4v9a3 3 0 0 0 3 3h9" />
                <path d="M15 13l3 3-3 3" />
              </svg>
              <span aria-hidden="true">{zeichen.unter.kurz}</span>
              <span className="nur-leser">{zeichen.unter.lang}</span>
            </span>
          ) : null}
          {zeichen.kommentare ? (
            <span className={s.zeichen}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 5h16v11H9l-5 4z" />
              </svg>
              <span aria-hidden="true">{zeichen.kommentare.kurz}</span>
              <span className="nur-leser">{zeichen.kommentare.lang}</span>
            </span>
          ) : null}
        </div>
        {meta.length ? <small className={s.meta}>{meta.join(" · ")}</small> : null}
      </div>
      <div role="none" className={s.mitte}>
        <div role="cell" className={s.cPrio}>
          <Wertwahl
            art="Priorität"
            name={`„${a.titel}“`}
            optionen={PRIO_ZEILE}
            wert={a.prioritaet}
            darf={bearbeitbar}
            klein
            breite={PRIO_BREITE}
            onWechsel={(neu) => speichern({ prioritaet: neu })}
          />
        </div>
        <div role="cell" className={s.cPersonen}>
          {onUebernehmen && a.personen.length === 0 && !lesen ? (
            <button type="button" className={s.uebernehmen} onClick={() => onUebernehmen(a)}>
              Übernehmen<span className="nur-leser"> „{a.titel}“</span>
            </button>
          ) : bearbeitbar ? (
            <PersonWahl a={a} personen={personen} sa={sa} />
          ) : (
            <span className={s.stapelNurLesen} title={a.personen.length ? undefined : "Offen"}>
              <Stapel personen={a.personen.map((id) => personNach(personen, id))} />
            </span>
          )}
        </div>
        <div role="cell" className={s.cFrist}>
          {bearbeitbar ? (
            <Kalender
              klein
              label={`Frist für „${a.titel}“`}
              knopfLabel={`${frist.vorlesen}, für „${a.titel}“`}
              knopfKlasse={s.fristKnopf}
              anzeige={fristInhalt}
              wert={a.frist_datum}
              entfernen="Frist entfernen"
              sprint={sprint}
              onWahl={(tag) => speichern({ frist_datum: tag })}
            />
          ) : (
            <span className={s.fristNurLesen}>
              <span aria-hidden="true">{fristInhalt}</span>
              <span className="nur-leser">{frist.vorlesen}</span>
            </span>
          )}
        </div>
        {ohneVorhaben ? null : (
          <div role="cell" className={s.cVorhaben}>
            {wieAufgabe ? (
              <span className={s.wieAufgabe}>wie Aufgabe</span>
            ) : (
              <>
                <span className={s.vorhabenPille}>
                  <Wertwahl
                    art="Vorhaben"
                    name={`„${a.titel}“`}
                    optionen={vorhabenOptionen}
                    wert={a.vorhaben_id ?? ""}
                    darf={darfVorhaben}
                    klein
                    fuellen
                    onWechsel={(neu) =>
                      speichern({ vorhaben_id: neu || null }, () => {
                        // Ohne Vorhaben gibt es keine Kapsel: Die Datenbank setzt dann „Nur Vorstand“ (wie im Dialog)
                        if (!neu && a.sichtbarkeit === "vorhaben") zeigen("Ohne Vorhaben gilt jetzt „Nur Vorstand“.");
                      })
                    }
                  />
                </span>
                {/* Am Handy: das Vorhaben als Text mit Farbpunkt unter dem Titel */}
                {a.vorhaben_id && vorhabenName ? (
                  <span className={s.vorhabenText}>
                    <span className={s.punkt} style={{ background: punktFarbe(vorhabenRessort, farben) }} aria-hidden="true" />
                    <span className="nur-leser">Vorhaben: </span>
                    {vorhabenName}
                  </span>
                ) : null}
              </>
            )}
          </div>
        )}
        <div role="cell" className={s.cRessort} data-knopf={sprintSpalte && !wieAufgabe && (a.sprint_status === "laufend" || (sprintSpalte.onInSprint && !lesen && !a.sprint_id && !fertig)) ? true : undefined}>
          {sprintSpalte ? (
            <SprintZelle a={a} istUnter={!!istUnter} lesen={lesen} onInSprint={sprintSpalte.onInSprint} />
          ) : wieAufgabe ? (
            <span className={s.wieAufgabe}>wie Aufgabe</span>
          ) : (
            <Wertwahl
              art="Ressort"
              name={`„${a.titel}“`}
              optionen={ressortOptionen}
              wert={a.ressort_id ?? ""}
              darf={bearbeitbar}
              klein
              fuellen
              onWechsel={(neu) => speichern({ ressort_id: neu || null })}
            />
          )}
        </div>
        {onInSprint ? (
          <div role="cell" className={s.cSprint}>
            {!istUnter && !lesen ? (
              <Knopf art="primaer" ohnePunkt klein onClick={() => onInSprint(a)}>
                In den Sprint<span className="nur-leser">: „{a.titel}“</span>
              </Knopf>
            ) : null}
          </div>
        ) : null}
      </div>
      <div role="cell" className={s.cMenue}>
        <Menue label={`Mehr zu „${a.titel}“`} punkte={punkte} />
      </div>
    </div>
  );
}

/** Letzte Zeile „+ Aufgabe hinzufügen“ (BAUPLAN-AUFGABENLISTE.md, Abschnitt 5, SYSTEM.md Abschnitt 12, wie ClickUp): ein Klick öffnet ein
 *  Feld in der Zeile. Enter legt mit den Vorgaben der Liste an, das Feld wird leer und bleibt offen für die nächste. Esc schließt, ebenso
 *  der Fokus woanders bei leerem Feld. Nur mit dem Recht zum Anlegen. */
function PlusZeile({ label, neu }: { label: string; neu: NeueZeile }) {
  const sitzung = useAngemeldet();
  const sprints = useSprints();
  const vorhaben = useVorhaben();
  const { anlegen, statusSofort } = useAufgabenAktionen();
  const { zeigen } = useHinweis();
  const [offen, setOffen] = useState(false);
  const [titel, setTitel] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [gemeldet, setGemeldet] = useState("");
  const feld = useRef<HTMLInputElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const hilfe = useId();
  // Nach Esc bekommt der Knopf „+ Aufgabe hinzufügen“ den Fokus zurück
  const zurueck = useRef(false);

  useEffect(() => {
    if (offen) feld.current?.focus();
    else if (zurueck.current) {
      zurueck.current = false;
      knopf.current?.focus();
    }
  }, [offen]);

  if (!sitzung.rechte.has("aufgaben.anlegen_offen")) return null;

  function schliessen(fokusZurueck: boolean) {
    setOffen(false);
    setTitel("");
    zurueck.current = fokusZurueck;
  }

  async function absenden() {
    const t = titel.trim().replace(/\s+/g, " ");
    if (!t || laeuft) return;
    const w = startwerte(neu.vorgabe, {
      ich: sitzung.person.id,
      vorstand: sitzung.rechte.has("aufgaben.alle_bearbeiten"),
      darfPlanen: sitzung.rechte.has("sprint.planen"),
      laufend: laufenderSprint(sprints.data),
      ressortVon: (id) => vorhaben.data?.find((x) => x.id === id)?.ressort_id,
    });
    // Feld sofort leeren, damit die nächste Aufgabe schon getippt werden kann. Geht es schief, kommt der Titel zurück.
    setTitel("");
    setLaeuft(true);
    try {
      const id = await anlegen.mutateAsync({
        titel: t,
        person: w.personen[0] ?? null,
        personen: w.personen,
        sprint: w.sprint,
        frist_datum: null,
        prioritaet: w.prioritaet,
        sichtbarkeit: w.sichtbarkeit,
        eltern: null,
        ressort: w.ressort,
        vorhaben: w.vorhaben,
      });
      // Gruppe nach Status: den Status der Gruppe gleich setzen (Zu tun ist schon der Status einer neuen Aufgabe)
      if (neu.status && neu.status !== "zu_tun") await statusSofort.mutateAsync({ id, status: neu.status });
      setGemeldet(`„${t}“ angelegt.`);
      neu.onAngelegt?.(id);
    } catch (err) {
      setTitel((jetzt) => jetzt || t);
      zeigen(fehlertext(err), { fehler: true });
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div role="row" className={[s.plusZeile, offen && s.plusOffen].filter(Boolean).join(" ")}>
      <div role="cell" className={s.plusZelle}>
        {offen ? (
          <>
            <span className={s.plusZeichen} aria-hidden="true">
              +
            </span>
            <input
              ref={feld}
              className={s.plusFeld}
              aria-label={`Neue Aufgabe in ${label}`}
              aria-describedby={hilfe}
              placeholder="Titel der Aufgabe"
              maxLength={200}
              value={titel}
              aria-busy={laeuft || undefined}
              onChange={(e) => setTitel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void absenden();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  schliessen(true);
                }
              }}
              onBlur={() => {
                if (!titel.trim() && !laeuft) schliessen(false);
              }}
            />
            <span id={hilfe} className={s.plusHilfe}>
              Enter legt an · Esc bricht ab
            </span>
          </>
        ) : (
          <button ref={knopf} type="button" className={s.plusKnopf} onClick={() => setOffen(true)}>
            <span aria-hidden="true">+</span> Aufgabe hinzufügen <span className="nur-leser">in {label}</span>
          </button>
        )}
        <span className="nur-leser" role="status">
          {gemeldet}
        </span>
      </div>
    </div>
  );
}

/** Zelle „Sprint“ in der Liste eines Vorhabens: „Im Sprint“ (Wort und Zeichen, nie nur Farbe), „In den Sprint“ oder „Backlog“. */
function SprintZelle({ a, istUnter, lesen, onInSprint }: { a: Aufgabe; istUnter: boolean; lesen: boolean; onInSprint: ((a: Aufgabe) => void) | undefined }) {
  if (istUnter || a.eltern_id) return <span className={s.wieAufgabe}>wie Aufgabe</span>;
  if (a.sprint_status === "laufend") {
    return (
      <span className={s.imSprint}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
        Im Sprint
      </span>
    );
  }
  if (a.sprint_status === "abgeschlossen") return <span className={s.wieAufgabe}>Archiv</span>;
  if (onInSprint && !lesen && a.status !== "erledigt") {
    return (
      <Knopf art="primaer" ohnePunkt klein onClick={() => onInSprint(a)}>
        In den Sprint<span className="nur-leser">: „{a.titel}“</span>
      </Knopf>
    );
  }
  return <span className={s.wieAufgabe}>Backlog</span>;
}

/** Link auf die Aufgabe mit der eigenen Adresse der App kopieren. /aufgaben/<id> öffnet den Dialog über Meine Woche. */
export async function linkKopieren(id: string, zeigen: ReturnType<typeof useHinweis>["zeigen"]) {
  const url = `${window.location.origin}/aufgaben/${id}`;
  try {
    await navigator.clipboard.writeText(url);
    zeigen("Link kopiert.");
  } catch {
    zeigen(`Kopieren ging nicht. Der Link: ${url}`, { fehler: true });
  }
}

/** Speicherfrist (Paket F, ADR-017): Die App löscht gelöschte Aufgaben ohne Vorhaben nach 12 Monaten endgültig. */
export const GELOESCHT_FRIST =
  "Wiederherstellen geht 12 Monate lang. Danach löscht die App gelöschte Aufgaben endgültig, Aufgaben eines Vorhabens erst mit dem Vorhaben.";

/** „Gelöscht (n)“ unter der Liste im Sprint und im Backlog: aufklappen, je Zeile Titel, wer wann gelöscht hat, „Wiederherstellen“. */
export function Geloescht({ liste, personen, loeschen }: { liste: readonly Aufgabe[]; personen: readonly PersonKurz[] | undefined; loeschen: Loeschen }) {
  const [auf, setAuf] = useState(false);
  if (liste.length === 0) return null;
  return (
    <div className={s.geloeschtBereich}>
      <button type="button" className={s.geloeschtKnopf} aria-expanded={auf} onClick={() => setAuf(!auf)}>
        Gelöscht ({liste.length})
      </button>
      {auf ? <p className={s.geloeschtHinweis}>{GELOESCHT_FRIST}</p> : null}
      {auf ? (
        <ul className={[ui.karte, s.geloeschtListe].join(" ")} aria-label="Gelöscht">
          {liste.map((a) => {
            const wer = personNach(personen, a.geaendert_von);
            return (
              <li key={a.id} className={s.geloeschtZeile}>
                <div className={s.geloeschtText}>
                  <b>{a.titel}</b>
                  <small>
                    {a.eltern_id ? `Unteraufgabe von „${a.eltern_titel ?? "…"}“ · ` : ""}gelöscht{wer ? ` von ${wer.vorname || anzeigename(wer)}` : ""}
                    {a.archiviert_am ? `, ${tagVon(a.archiviert_am)}` : ""}
                  </small>
                </div>
                <Knopf klein onClick={() => loeschen.wiederherstellen(a)}>
                  Wiederherstellen<span className="nur-leser"> „{a.titel}“</span>
                </Knopf>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** Gelöschte Aufgaben eines Bereichs, ohne Unteraufgaben, die mit ihrer Aufgabe gelöscht wurden */
export function geloeschteIn(alle: readonly Aufgabe[], passt: (a: Aufgabe) => boolean): Aufgabe[] {
  const geloeschteIds = new Set(alle.filter((a) => a.archiviert_am).map((a) => a.id));
  return alle
    .filter((a) => a.archiviert_am && passt(a) && !(a.eltern_id && geloeschteIds.has(a.eltern_id)))
    .sort((x, y) => (y.archiviert_am ?? "").localeCompare(x.archiviert_am ?? ""));
}
